import {
  ContextMode,
  NavItem,
  NavSection,
  SidebarState,
} from '@components/v2/layout/AppTemplate/appTemplate.types';
import Icon from '@components/v2/ui/Icon/Icon';
import List from '@components/v2/ui/List/List';
import ListItemButton from '@components/v2/ui/List/ListItemButton';
import ListItemIcon from '@components/v2/ui/List/ListItemIcon';
import ListItemText from '@components/v2/ui/List/ListItemText';
import ListSubheader from '@components/v2/ui/List/ListSubheader';
import { Box, Drawer } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../../modules/translation/TranslationContext';
import { AppTemplateTestIds } from '../../../../../../utils/testIds/appTemplate.testIds';
import SidebarNavItem from './components/SidebarNavItem/SidebarNavItem';
import { useSidebarNavigation } from './hooks/useSidebarNavigation';
import { useSidebarStyles } from './sidebar.styles';

interface SidebarProps {
  navItems: NavSection[];
  subContextNavItems?: NavSection[];
  bottomSlot?: ReactNode;
  subContextTopSlot?: ReactNode;
  sidebarState: SidebarState;
  contextMode: ContextMode;
  topSlotHeight?: number;
  className?: string;
  mobile?: {
    isMobile: boolean;
    isOpen: boolean;
    onClose: () => void;
  };
  onBack?: () => void;
}

const Sidebar = ({
  navItems,
  subContextNavItems = [],
  bottomSlot,
  subContextTopSlot,
  sidebarState,
  contextMode,
  topSlotHeight = 0,
  className,
  mobile,
  onBack,
}: SidebarProps) => {
  const isMobile = mobile?.isMobile ?? false;
  const isMobileOpen = mobile?.isOpen ?? false;
  const onClose = mobile?.onClose;
  const { translate } = useTranslation();
  const { classes, cx } = useSidebarStyles({ sidebarState, isMobile, topSlotHeight });
  const { isItemActive, handleNavItemClick } = useSidebarNavigation({
    onCloseMobileMenu: onClose,
  });
  const isCollapsed = !isMobile && sidebarState === 'collapsed';
  const isNested = contextMode === 'nested';

  const renderNavItems = (items: NavItem[]) =>
    items
      .filter((item) => !item.hidden)
      .map((item) => (
        <SidebarNavItem
          key={item.pathname ?? item.label}
          item={item}
          isCollapsed={isCollapsed}
          isActive={isItemActive(item)}
          onClick={handleNavItemClick}
        />
      ));

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? isMobileOpen : true}
      onClose={isMobile ? onClose : undefined}
      className={className}
      slotProps={{
        paper: { className: classes.drawerPaper },
        backdrop: { invisible: true },
      }}
    >
      <Box className={classes.inner}>
        <Box className={classes.navRegion}>
          {[
            {
              sections: navItems,
              hidden: isNested,
              testId: AppTemplateTestIds.NAV_LAYER_MAIN,
              showBack: false,
              topSlot: null,
            },
            {
              sections: subContextNavItems,
              hidden: !isNested,
              testId: AppTemplateTestIds.NAV_LAYER_SUB,
              showBack: isMobile,
              topSlot: subContextTopSlot,
            },
          ].map(({ sections, hidden, testId, showBack, topSlot }) => (
            <Box
              key={testId}
              test-id={testId}
              aria-hidden={hidden}
              className={cx(
                classes.navLayer,
                hidden ? classes.navLayerHidden : classes.navLayerVisible,
              )}
            >
              {!isCollapsed && (showBack || topSlot) && (
                <Box className={classes.stickyTopSlot}>
                  {showBack && (
                    <ListItemButton onClick={onBack} className={classes.backButton}>
                      <ListItemIcon>
                        <Icon icon={ArrowLeft} />
                      </ListItemIcon>
                      <ListItemText primary={translate(LangEnum.GLOBAL_BACK)} />
                    </ListItemButton>
                  )}
                  {topSlot}
                </Box>
              )}
              <List>
                {sections.flatMap((section, idx) =>
                  section.title
                    ? [
                        <Fragment key={section.title}>
                          <ListSubheader
                            className={cx(
                              classes.sectionTitle,
                              idx > 0 && classes.sectionTitleSpaced,
                            )}
                          >
                            {section.title}
                          </ListSubheader>
                          {renderNavItems(section.items)}
                        </Fragment>,
                      ]
                    : renderNavItems(section.items),
                )}
              </List>
            </Box>
          ))}
        </Box>
        {bottomSlot && <Box className={classes.bottomSlot}>{bottomSlot}</Box>}
      </Box>
    </Drawer>
  );
};

export default Sidebar;
